import { BoxGeometry, Group, Mesh } from 'three';
import type {
  HouseBodyConfig,
  SlabHoleConfig,
  StoryConfig,
  TerraceConfig,
} from '@/model/schema';
import type { MaterialKit } from '../materials';
import {
  buildStoryWalls,
  buildInteriorPartitions,
  type FacadeDecor,
  type HingeCollector,
} from './buildWalls';

/**
 * Build a single story: floor slab + exterior walls + interior partitions.
 *
 * The returned group is named `Story_<level>` so the scene controller can
 * hide stories independently for peel view without touching canonical state.
 *
 * Slab cut-outs (`story.slabHoles`) are punched into the floor by splitting
 * it into a 4-strip donut around each hole. With one hole (the typical
 * case — one stair per inter-story junction) this produces clean geometry;
 * multiple holes are handled by iterating the strips, accepting some
 * redundant overlap that's covered by depth buffer.
 */
export function buildStory(
  body: HouseBodyConfig,
  story: StoryConfig,
  kit: MaterialKit,
  decor: FacadeDecor,
  hinges: HingeCollector,
  terraces: TerraceConfig[] = [],
): Group {
  const group = new Group();
  group.name = `Story_${story.level}`;
  group.position.y = story.elevation;

  const isGround = story.level === 0;
  // Slab inset: ground keeps its big inset so the floor sits between the
  // exterior walls (no z-fighting with the wall outer face). Upper-story
  // slabs USED to cantilever 2 cm outward (inset = -0.02), which produced
  // a heavy horizontal shadow band at every story line because the lip
  // blocked sun from the wall just beneath it (user-reported "black gap
  // between stories"). We now tuck the upper slabs slightly INSIDE the
  // exterior walls instead, so the wall surface is continuous from L0
  // into L1 and no shadow strip can form.
  const upperInset = 0.01;
  const inset = isGround ? body.exteriorWallThickness + 0.01 : upperInset;
  const slabWidth = Math.max(0.1, body.footprintWidth - 2 * inset);
  const slabDepth = Math.max(0.1, body.footprintDepth - 2 * inset);
  const slabOriginX = inset;
  const slabOriginZ = inset;
  const slabY = isGround ? body.floorThickness / 2 : 0;

  // Convert per-story slabHoles (in body-local coords) into the slab's local
  // coord system (origin at slabOriginX, slabOriginZ).
  const holes: Array<SlabHoleConfig> = story.slabHoles.map((h) => ({
    x: h.x - slabOriginX,
    z: h.z - slabOriginZ,
    width: h.width,
    depth: h.depth,
  }));

  addCutSlab(group, kit, slabWidth, slabDepth, slabY, body.floorThickness, holes);

  group.add(buildStoryWalls(body, story, kit, decor, hinges, terraces));
  group.add(buildInteriorPartitions(body, story, kit, hinges));

  // Position the story so that body origin lines up. The house body's
  // (originX, originZ) is its lower-left corner in world XZ. We translate
  // the story root so that (0,0) in body-local equals body center.
  group.position.x = body.originX + body.footprintWidth / 2;
  group.position.z = body.originZ + body.footprintDepth / 2;

  return group;
}

/**
 * Build the floor slab as a grid of rectangles, with cells inside any
 * `holes` rectangle omitted. `slabOriginX/Z` is implicit — we receive
 * the holes already converted to slab-local coords (origin at slab's
 * lower-left corner) and emit strips centred on the body origin.
 *
 * Why a grid (not the previous 4-strip-per-hole pass)
 * --------------------------------------------------
 * The old per-hole 4-strip cut emitted strips that filled THE WHOLE
 * SLAB except for that one hole. With two holes (e.g. L0→L1 stair well
 * AND L0→Basement stair well, both of which we punch through L0's slab)
 * each hole's strips happily filled the OTHER hole's gap, so neither
 * hole was visible. User report: "the floor is still not cut for the
 * basement stairs." Bug confirmed by inspection.
 *
 * The grid decomposition is robust for any N rectangular holes: collect
 * every X and Z edge into a sorted unique list, walk the resulting
 * cells, and emit a slab for every cell that doesn't lie strictly
 * inside any hole. O((N+1)^2) cells in the worst case which is fine
 * for the handful of holes a story will ever have.
 */
function addCutSlab(
  parent: Group,
  kit: MaterialKit,
  slabWidth: number,
  slabDepth: number,
  slabY: number,
  floorThickness: number,
  holes: SlabHoleConfig[],
) {
  if (holes.length === 0) {
    const mesh = new Mesh(
      new BoxGeometry(slabWidth, floorThickness, slabDepth),
      kit.interiorFloor,
    );
    mesh.name = 'FloorSlab';
    // The slab MUST cast as well as receive — without castShadow the
    // directional light passes straight through it and projects shadows
    // of upper-story objects onto everything below, producing a striped
    // "light shines through" pattern on the lower-story floor.
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(0, slabY, 0);
    parent.add(mesh);
    return;
  }

  // Collect unique x/z edges from the slab outline and every hole.
  const clamped = holes.map((h) => ({
    xLo: clamp(h.x, 0, slabWidth),
    xHi: clamp(h.x + h.width, 0, slabWidth),
    zLo: clamp(h.z, 0, slabDepth),
    zHi: clamp(h.z + h.depth, 0, slabDepth),
  })).filter((h) => h.xHi - h.xLo > 0.01 && h.zHi - h.zLo > 0.01);

  if (clamped.length === 0) {
    const mesh = new Mesh(new BoxGeometry(slabWidth, floorThickness, slabDepth), kit.interiorFloor);
    mesh.name = 'FloorSlab';
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(0, slabY, 0);
    parent.add(mesh);
    return;
  }

  const xEdges = uniqueSorted([0, slabWidth, ...clamped.flatMap((h) => [h.xLo, h.xHi])]);
  const zEdges = uniqueSorted([0, slabDepth, ...clamped.flatMap((h) => [h.zLo, h.zHi])]);

  const baseX = -slabWidth / 2;
  const baseZ = -slabDepth / 2;
  let strip = 0;

  for (let i = 0; i < xEdges.length - 1; i++) {
    for (let j = 0; j < zEdges.length - 1; j++) {
      const x0 = xEdges[i]!;
      const x1 = xEdges[i + 1]!;
      const z0 = zEdges[j]!;
      const z1 = zEdges[j + 1]!;
      const cellW = x1 - x0;
      const cellD = z1 - z0;
      if (cellW < 0.01 || cellD < 0.01) continue;
      const cxCell = (x0 + x1) / 2;
      const czCell = (z0 + z1) / 2;
      // Skip if the cell centre falls inside any hole.
      const inHole = clamped.some(
        (h) => cxCell > h.xLo && cxCell < h.xHi && czCell > h.zLo && czCell < h.zHi,
      );
      if (inHole) continue;
      addStrip(
        parent,
        kit,
        cellW,
        cellD,
        baseX + cxCell,
        slabY,
        baseZ + czCell,
        floorThickness,
        `FloorSlab_${strip.toString().padStart(3, '0')}`,
      );
      strip++;
    }
  }
}

function uniqueSorted(values: number[]): number[] {
  const sorted = values.slice().sort((a, b) => a - b);
  const out: number[] = [];
  for (const v of sorted) {
    if (out.length === 0 || v - out[out.length - 1]! > 1e-4) out.push(v);
  }
  return out;
}

function addStrip(
  parent: Group,
  kit: MaterialKit,
  sx: number,
  sz: number,
  cx: number,
  cy: number,
  cz: number,
  floorThickness: number,
  name: string,
) {
  if (sx <= 0 || sz <= 0) return;
  const mesh = new Mesh(new BoxGeometry(sx, floorThickness, sz), kit.interiorFloor);
  mesh.name = name;
  // Same rationale as the un-cut slab — see addCutSlab() above.
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.position.set(cx, cy, cz);
  parent.add(mesh);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
